import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { AthleteStatus } from "./athleteTypes";

export type Athlete = {
  user_id: number;
  email: string | null;
  telegram_id: number | null;
  has_coach: boolean;
  coach: {
    id: number;
    name: string;
    away_until?: string | null;
    away_message?: string | null;
    is_currently_away?: boolean;
  } | null;
  status: AthleteStatus | string;
  onboarding_intro_seen?: boolean;
  first_name?: string | null;
  last_name?: string | null;
  email_verified?: boolean;
  // R6: pending email change. While these are set, .email is still the
  // verified working address; banner asks user to confirm pending_email.
  pending_email?: string | null;
  pending_email_expires?: string | null;
  // ТД-90: при TG-widget-логине, создавшем новый аккаунт, бэк присылает
  // suggest_link=true — фронт показывает banner «возможно у тебя уже есть
  // email-аккаунт».
  suggest_link?: boolean;
  is_new_account?: boolean;
  // Z5/Phase 1 — resolved server-side via get_athlete_effective_tier.
  // "coached" = bundled Pro-level access via an active coach link, never
  // down-gated. See shared/services/athlete_tier.py.
  tier?: "free" | "pro" | "elite" | "coached";
};

export type LinkTelegramPayload = {
  id: number;
  auth_date: number;
  hash: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
};

export function useLinkTelegramWidget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: LinkTelegramPayload) => {
      const r = await api.post<{ ok: true }>("/athlete/auth/link-telegram", data);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

// Routes where the user is guaranteed unauthenticated — skipping /me there
// removes the 401 console noise (browser logs failed XHR regardless of
// try/catch) and shaves one network round-trip from the LCP path.
const PREAUTH_ROUTES = [
  "/login", "/forgot-password", "/reset-password", "/verify-email",
  "/welcome", "/founding", "/invite", "/pricing",
  "/terms", "/impressum", "/contact", "/datenschutz", "/agb", "/widerruf",
];

function isPreAuthRoute(): boolean {
  const p = typeof window !== "undefined" ? window.location.pathname : "";
  return PREAUTH_ROUTES.some(r => p === r || p.startsWith(r + "/"));
}

export function useMe() {
  return useQuery<Athlete | null>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        const r = await api.get<Athlete>("/athlete/auth/me");
        return r.data;
      } catch {
        return null;
      }
    },
    enabled: !isPreAuthRoute(),
    retry: false,
    staleTime: 60_000,
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      lang?: string;
      // W4.3 — DSGVO consent gate. Backend rejects with 400 if absent.
      consent_accepted: boolean;
    }) => {
      const r = await api.post<Athlete>("/athlete/auth/register", vars);
      return r.data;
    },
    onSuccess: (data) => qc.setQueryData(["me"], data),
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string }) => {
      const r = await api.post<Athlete>("/athlete/auth/login", vars);
      return r.data;
    },
    onSuccess: (data) => qc.setQueryData(["me"], data),
  });
}

export function useTelegramLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      initData: string;
      // W4.6 — DSGVO consent gate. Required for first-time creation; existing
      // TG users keep working without it (backend only gates the new-account
      // branch). The wrapper page collects the checkbox before submit.
      consent_accepted: boolean;
    }) => {
      const r = await api.post<Athlete>(
        "/athlete/auth/telegram",
        { init_data: vars.initData, consent_accepted: vars.consent_accepted },
      );
      return r.data;
    },
    onSuccess: (data) => qc.setQueryData(["me"], data),
  });
}

export type TelegramWidgetUser = {
  id: number;
  auth_date: number;
  hash: string;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
};

export function useTelegramWidgetLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      data: TelegramWidgetUser & {
        // W4.4 — DSGVO consent gate. The wrapper page collects the checkbox
        // before opening the widget; the value is forwarded. Backend rejects
        // new-account creation with 400 when this is false.
        consent_accepted: boolean;
      },
    ) => {
      const r = await api.post<Athlete>("/athlete/auth/telegram-widget", data);
      return r.data;
    },
    onSuccess: (data) => qc.setQueryData(["me"], data),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (vars: { email: string; lang?: string }) => {
      await api.post("/athlete/auth/forgot-password", {
        email: vars.email,
        lang: vars.lang || "en",
      });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (vars: { token: string; new_password: string }) => {
      await api.post("/athlete/auth/reset-password", vars);
    },
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (token: string) => {
      await api.get("/athlete/auth/verify-email", { params: { token } });
    },
  });
}

export function useChangeEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { new_email: string }) => {
      const r = await api.post<{
        verification_sent: boolean;
        pending_email: string;
        expires_at: string | null;
      }>("/athlete/auth/change-email", vars);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useCancelEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/athlete/auth/cancel-email-change");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await api.post("/athlete/auth/logout");
    },
    onSuccess: () => {
      qc.setQueryData(["me"], null);
      qc.clear();
    },
  });
}
